import React, { useContext } from "react";
import { useFormikContext } from "formik";
import "./confirm-request-v2.scss";

import { channelOptions } from "../../../core/config/requests";
import { getBaseCurrency, isMulticurrency, baseCurrencyOptions } from "../../../core/currency";
import { SettingsContext, UserContext } from "../../../contexts";
import { Visible } from "../../../core/components";
import { FieldGroup } from "../../../components";
import { Input, NumberInput, Select } from "../../../components/ui";
import { boxOptions } from "../../../product/constants";
import { requestTypes } from "../../../requests/constants";
import { useV2Mode } from "../../../requests/v2/V2ModeContext";

const conditionOptions = [
  { id: "new", name: "New (Brand new, without signs of wear)" },
  { id: "excellent", name: "Excellent (Very faint handling marks only)" },
  {
    id: "very-good",
    name: "Very Good (Few signs of wear and tear, with light scratches to the case and bracelet without any visible dents)",
  },
  { id: "good", name: "Good (Visible signs of wear, scuffs or small dents)" },
  {
    id: "fair",
    name: "Fair (Significant age-related wear with obvious scratches and dents)",
  },
];

const currentYear = new Date().getFullYear();
const yearOptions = [...Array(50)].map((_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

const conditionSelectOptions = conditionOptions.map((o) => ({
  value: o.id,
  label: o.name.split("(")[0].trim(),
}));

const ROW_STYLE = {
  display: "grid",
  gap: 8,
  marginBottom: 8,
};

// `.ft-select` carries a 180px min-width for standalone use. These rows are a
// grid whose `fr` tracks take their automatic minimum from the item, so that
// floor would widen the currency / year columns and overflow the pane — the
// react-select it replaces had no floor. Let the track own the width instead.
const SELECT_STYLE = { minWidth: 0 };

// V2 money input connected to Formik. The Formik value stays in minor units
// (pence); NumberInput speaks major units, so the /100 and the *100 round-trip
// here exactly as the CurrencyInput it replaces did.
const V2Money = ({ name, placeholder, disabled }) => {
  const { values, setFieldValue, handleBlur, errors, touched } =
    useFormikContext();
  const stored = values[name];

  return (
    <NumberInput
      money
      currency={values.currency}
      size="md"
      fullWidth
      maxLength={9}
      placeholder={placeholder}
      aria-label={placeholder}
      value={stored ? stored / 100 : null}
      disabled={disabled}
      invalid={!!(touched?.[name] && errors?.[name])}
      onChange={(val) =>
        setFieldValue(name, val == null ? null : Math.round(val * 100))
      }
      onBlur={handleBlur(name)}
    />
  );
};

// V2 single/multi select connected to Formik
const V2Select = ({ name, options, placeholder, isMulti = false }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const stored = values[name];

  // Multi values are re-derived in `options` order and unknown ids dropped —
  // the order react-select rendered them in, and the order Formik ends up
  // storing after the next pick.
  const value = isMulti
    ? options.filter((o) => (stored || []).includes(o.value)).map((o) => o.value)
    : stored || null;

  return (
    <Select
      options={options}
      placeholder={placeholder}
      label={placeholder}
      size="md"
      fullWidth
      // Every V2 select was type-to-filter under react-select. Fontaine only
      // turns its filter on above 8 options, which would have silently dropped
      // it from CONDITION, DELIVERY SET and CURRENCY — so ask for it outright.
      searchable
      style={SELECT_STYLE}
      multi={isMulti}
      value={value}
      invalid={!!(touched?.[name] && errors?.[name])}
      onChange={(sel) => setFieldValue(name, isMulti ? sel || [] : sel ?? null)}
    />
  );
};

// V2-specific form — Fontaine primitives on a plain grid, no FieldGroup / Bootstrap
const RequestFormV2 = ({ isOrder, paidOffline, requestType, isEditing }) => {
  const { isMerchant } = useContext(SettingsContext);
  const { user } = useContext(UserContext);

  // Multi-currency: an editable currency dropdown (base-eligible, defaults to
  // store base) precedes the budget / price-expectation fields. Hidden while
  // the flag is off so legacy layout/behaviour is unchanged.
  const mc = isMulticurrency();
  const currencySelectOptions = baseCurrencyOptions().map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="request-form request-form--v2">
      {isOrder ? (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: mc ? "0.7fr 1fr 1fr 1fr" : "1fr 1fr 1fr" }}>
          {mc && <V2Select name="currency" options={currencySelectOptions} placeholder="Currency" />}
          <V2Money name="minBudget" placeholder="Min budget" />
          <V2Money name="maxBudget" placeholder="Max budget" />
          <V2Select name="year" options={yearOptions} placeholder="From year" />
        </div>
      ) : (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: mc ? "0.7fr 2fr 1fr" : "2fr 1fr" }}>
          {mc && <V2Select name="currency" options={currencySelectOptions} placeholder="Currency" />}
          <V2Money
            name="priceExpectation"
            placeholder={
              requestType === requestTypes.STOCK
                ? "QUOTED PRICE"
                : "PRICE EXPECTATION"
            }
          />
          <V2Select name="year" options={yearOptions} placeholder="Year" />
        </div>
      )}

      {isOrder ? (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: "1fr" }}>
          <V2Select
            name="condition"
            options={conditionSelectOptions}
            placeholder="Condition"
            isMulti
          />
        </div>
      ) : (
        <>
          <div style={{ ...ROW_STYLE, gridTemplateColumns: "1fr" }}>
            <V2Select
              name="condition"
              options={conditionSelectOptions}
              placeholder="Condition"
            />
          </div>
          <div style={{ ...ROW_STYLE, gridTemplateColumns: "1fr" }}>
            <V2Select
              name="deliverySet"
              options={boxOptions.map((o) => ({ value: o.id, label: o.name }))}
              placeholder="Delivery set"
            />
          </div>
        </>
      )}

      {/* CORRECT use of `is_dao_admin` (= `is_admin && !is_merchant`) for both
          blocks below: these are internal request-desk fields on the store's own
          request, not RC-internal data. CHANNEL records how the lead reached the
          store (WhatsApp / Instagram / Dealer / …) — every tenant needs it, a
          merchant submitting a request does not. QUOTED PRICE + SERIAL NUMBER
          are the paid-offline reconciliation fields the admin taking the payment
          fills in. Do not tighten either to is_master_admin. */}
      {user?.is_dao_admin && !isMerchant && (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: "1fr" }}>
          <V2Select
            name="channel"
            options={channelOptions.map((o) => ({ value: o, label: o }))}
            placeholder="Channel"
          />
        </div>
      )}

      {user?.is_dao_admin && paidOffline && (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: "1fr 1fr" }}>
          <V2Money name="quotedPrice" placeholder="Quoted price" disabled={isEditing} />
          {/* Unbound on purpose — carried over verbatim from the raw input this
              replaces: no Formik name, no value, so it never reaches the
              payload. M41A is the visual migration, not that repair. */}
          <Input
            type="text"
            size="md"
            fullWidth
            placeholder="Serial number"
            aria-label="SERIAL NUMBER"
            disabled={isEditing}
          />
        </div>
      )}
    </div>
  );
};

// Custom value containers used in the legacy (non-V2) form only. They are
// react-select `components`, so those fields pass `legacySelect`.
const YearValueContainer = ({ children, ...props }) => {
  const [value] = props.getValue();
  if (!value) return children;
  return <div className="selected-values">{value.name} +</div>;
};

const ConditionValueContainer = ({ children, ...props }) => {
  const values = props.getValue();
  if (values.length < 1) return children;
  let label;
  switch (values.length) {
    case 1:
      label = values[0].name?.split("(")?.[0]?.trim();
      break;
    case 2:
      label = values.map((v) => v.name.split("(")[0].trim()).join(" - ");
      break;
    default:
      label = "New or Very good or Worn";
  }
  return <div className="selected-values">{label}</div>;
};

const Option = (props) => {
  const { children, className, cx, getStyles, isDisabled, isFocused, isSelected, innerRef, innerProps } = props;
  return (
    <div
      css={getStyles("option", props)}
      className={cx({ option: true, "option--is-disabled": isDisabled, "option--is-focused": isFocused, "option--is-selected": isSelected, "rs-option": true }, className)}
      ref={innerRef}
      aria-disabled={isDisabled}
      {...innerProps}
    >
      <span className="option-checkbox">
        <i className={`option-check ${isSelected ? "active" : ""}`}>&nbsp;</i>
      </span>
      {children}
    </div>
  );
};

const legacyConditionOptions = conditionOptions.map((o) => ({ id: o.id, name: o.name }));
const legacyYearOptions = yearOptions.map((o) => ({ id: o.value, name: o.label }));

const RequestForm = ({
  isOrder = true,
  isEditing = false,
  paidOffline = false,
  requestType = requestTypes.ORDER,
}) => {
  const isV2 = useV2Mode();
  const { isMerchant } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const { values } = useFormikContext();

  if (isV2) {
    return (
      <RequestFormV2
        isOrder={isOrder}
        isEditing={isEditing}
        paidOffline={paidOffline}
        requestType={requestType}
      />
    );
  }

  // Multi-currency: the money-field display currency follows the form's own
  // `currency` field, and an editable base-eligible dropdown precedes the
  // budgets. Hidden / GBP-fixed while the flag is off.
  const mc = isMulticurrency();
  const reqCurrency = (values?.currency || getBaseCurrency()).toUpperCase();
  const legacyCurrencyOptions = baseCurrencyOptions();

  return (
    <div className="request-form">
      <div className="row">
        {mc && (
          <div className="col-12 col-xl-4 left-col">
            <FieldGroup name="currency" type="select" options={legacyCurrencyOptions} placeholder="Currency" />
          </div>
        )}
        <Visible when={isOrder}>
          <div className="col-6 col-xl-4 left-col min-budget">
            <FieldGroup name="minBudget" type="money" currency={reqCurrency} placeholder="Min budget" />
          </div>
          <div className="col-6 col-xl-4 max-budget">
            <FieldGroup name="maxBudget" type="money" currency={reqCurrency} placeholder="Max budget" />
          </div>
          <div className="col-12 col-xl-4 right-col">
            <FieldGroup name="year" type="select" options={legacyYearOptions} placeholder="From year" components={{ ValueContainer: YearValueContainer }} legacySelect />
          </div>
        </Visible>
        <Visible when={!isOrder}>
          <div className="col-12 col-xl-8 min-budget">
            <FieldGroup
              name="priceExpectation"
              type="money"
              currency={reqCurrency}
              placeholder={requestType === requestTypes.STOCK ? "QUOTED PRICE" : "PRICE EXPECTATION"}
            />
          </div>
          <div className="col-12 col-xl-4 right-col">
            <FieldGroup name="year" type="select" options={legacyYearOptions} placeholder="Year" />
          </div>
        </Visible>
      </div>

      <Visible when={isOrder}>
        <div className="row">
          <div className="col-12">
            <FieldGroup name="condition" type="select" isMulti options={legacyConditionOptions} placeholder="Condition" components={{ ValueContainer: ConditionValueContainer, Option }} hideSelectedOptions={false} legacySelect />
          </div>
        </div>
      </Visible>

      <Visible when={!isOrder}>
        <div className="row">
          <div className="col-12">
            <FieldGroup name="condition" type="select" options={legacyConditionOptions} placeholder="Condition" components={{ ValueContainer: ConditionValueContainer, Option }} hideSelectedOptions={false} legacySelect />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <FieldGroup name="deliverySet" type="select" options={boxOptions} placeholder="Delivery set" hideSelectedOptions={false} />
          </div>
        </div>
      </Visible>

      {/* Legacy (non-V2) layout of the same two blocks — see the comment on the
          V2 CHANNEL / paid-offline fields above. `is_dao_admin` is correct here:
          internal request-desk fields on the store's own request. */}
      <Visible when={user?.is_dao_admin && !isMerchant}>
        <div className="row">
          <div className="col-12">
            <FieldGroup name="channel" type="select" options={channelOptions} placeholder="Channel" hideSelectedOptions={false} />
          </div>
        </div>
      </Visible>

      <Visible when={user?.is_dao_admin && paidOffline}>
        <div className="row">
          <div className="col-12 col-xl-6 min-budget">
            <FieldGroup name="quotedPrice" type="money" currency={reqCurrency} placeholder="Quoted price" disabled={isEditing} />
          </div>
          <div className="col-12 col-xl-6 right-col">
            <FieldGroup name="serialNumber" type="text" placeholder="Serial number" disabled={isEditing} />
          </div>
        </div>
      </Visible>
    </div>
  );
};

export default RequestForm;
